import React, { useEffect, useState } from "react";
import { Loader2, CloudSun, History, Scissors, ShieldCheck, MapPin } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { OriginBadge } from "../../lib/origin.jsx";
import { getSku } from "../../lib/classificationApi";
import { formatNumber, cn } from "../../lib/utils";
import InstallBaseTree from "./InstallBaseTree";
import ClassExplanation from "./ClassExplanation";

const tick = { fill: "rgb(var(--muted))", fontSize: 9, fontFamily: "IBM Plex Mono" };
const ttStyle = { background: "rgb(var(--surface))", border: "1px solid rgb(var(--border-strong))", fontSize: 11, borderRadius: 2 };

function Section({ icon: Icon, title, aside, children, testid, className }) {
  return (
    <div data-testid={testid} className={cn("border border-border rounded-sm bg-bg/60 p-3", className)}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted inline-flex items-center gap-1.5"><Icon size={11} className="text-primary" />{title}</span>
        {aside}
      </div>
      {children}
    </div>
  );
}

function Timeline({ d }) {
  const hasLegacy = d.legacy_parts.length > 0;
  return (
    <Section icon={History} title="Demand timeline · weekly units" testid="sku-detail-timeline"
             aside={<span className="text-[10px] text-subtle">{hasLegacy ? "Legacy history shaded" : "No legacy history"} · {d.row.capped_days} capped days{d.cap_value ? ` (cap ${d.cap_value}/day)` : ""}</span>}>
      <div style={{ height: 190 }}>
        <ResponsiveContainer>
          <ComposedChart data={d.timeline} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="rgb(var(--border))" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="week" tick={tick} interval={12} tickFormatter={w => w.slice(0, 7)} />
            <YAxis yAxisId="u" tick={tick} />
            <YAxis yAxisId="c" orientation="right" hide domain={[0, dataMax => Math.max(4, dataMax * 3)]} />
            <Tooltip contentStyle={ttStyle} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Area yAxisId="u" type="stepAfter" dataKey="legacy" stackId="h" name="Legacy part" stroke="rgb(var(--info))" fill="rgb(var(--info))" fillOpacity={0.35} isAnimationActive={false} />
            <Area yAxisId="u" type="stepAfter" dataKey="own" stackId="h" name="This SKU (raw)" stroke="rgb(var(--chart-1))" fill="rgb(var(--chart-1))" fillOpacity={0.18} isAnimationActive={false} />
            <Line yAxisId="u" type="monotone" dataKey="processed" name="Processed (merged + capped)" stroke="rgb(var(--fg))" strokeWidth={1.4} dot={false} isAnimationActive={false} />
            <Bar yAxisId="c" dataKey="capped_days" name="Capped days" fill="rgb(var(--critical))" barSize={3} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {hasLegacy && (
        <div className="mt-2 text-[10.5px] text-muted space-y-0.5" data-testid="sku-detail-legacy">
          {d.legacy_parts.map(l => (
            <div key={l.sku}><span className="font-mono-tabular text-fg">{l.sku}</span> {l.name} · {l.type || "direct"} · ratio {l.ratio}{l.effective_date ? ` · replaced ${l.effective_date}` : ""}</div>
          ))}
        </div>
      )}
      {d.capped.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid="sku-detail-capped">
          <Scissors size={10} className="text-critical mt-0.5" />
          {d.capped.slice(0, 8).map(c => (
            <span key={c.date} className="font-mono-tabular text-[9.5px] px-1.5 py-0.5 border border-critical/30 bg-critical/5 rounded-sm">{c.date}: {c.raw} → {c.capped_to}</span>
          ))}
        </div>
      )}
    </Section>
  );
}

function Heat({ d }) {
  const r = d.row;
  const strong = (r.weather_r2 ?? 0) >= d.min_r2 && (r.weather_slope ?? 0) > 0;
  return (
    <Section icon={CloudSun} title={`Heat overlay · Cooling Degree Days above ${d.trigger_c}°C`} testid="sku-detail-heat"
             aside={<span className="inline-flex items-center gap-1.5"><span className={cn("text-[10px] font-mono-tabular", strong ? "text-success" : "text-muted")}>R² {r.weather_r2 ?? "—"}</span>
               <OriginBadge origin={d.weather.origin} source={d.weather.source} testid="sku-detail-weather-origin" /></span>}>
      <div style={{ height: 170 }}>
        <ResponsiveContainer>
          <ComposedChart data={d.heat} margin={{ top: 4, right: 0, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="rgb(var(--border))" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="month" tick={tick} interval={3} />
            <YAxis yAxisId="d" tick={tick} />
            <YAxis yAxisId="t" orientation="right" tick={tick} />
            <Tooltip contentStyle={ttStyle} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar yAxisId="d" dataKey="demand" name="Monthly demand" fill="rgb(var(--chart-1))" fillOpacity={0.55} isAnimationActive={false} />
            <Line yAxisId="d" type="monotone" dataKey="fitted" name="Heat-explained" stroke="rgb(var(--success))" strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            <Line yAxisId="t" type="monotone" dataKey="cdd" name="CDD" stroke="rgb(var(--warning))" strokeWidth={1.6} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10.5px] text-muted mt-1.5 leading-snug" data-testid="sku-detail-heat-text">
        {r.intermittent ? "Z-Intermittent SKUs skip the weather test." : strong
          ? `Heat explains ${(r.weather_r2 * 100).toFixed(0)}% of monthly demand variation (threshold ${d.min_r2}).${r.weather_reclassified ? ` Reclassified ${r.xyz_original} → ${r.xyz_final}.` : ""}`
          : `Heat explains ${((r.weather_r2 || 0) * 100).toFixed(0)}% of demand variation — below the ${d.min_r2} threshold, so the class is unchanged.`}
      </div>
    </Section>
  );
}

function SafetyStock({ d }) {
  const s = d.safety_stock, r = d.row;
  return (
    <Section icon={ShieldCheck} title="Safety-stock days explained" testid="sku-detail-safety">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2 text-[10.5px]">
        <div><div className="text-subtle">Supply</div><div className="font-medium">{s.supply}</div></div>
        <div><div className="text-subtle">Supplier</div><div className="font-medium truncate" title={s.supplier}>{s.supplier}</div></div>
        <div><div className="text-subtle">Lead time</div><div className="font-mono-tabular">{s.lead_time} days</div></div>
        <div><div className="text-subtle">Service level</div><div className="font-mono-tabular">{(r.service_level * 100).toFixed(0)}% (z {s.z})</div></div>
      </div>
      <ul className="space-y-1 text-[11.5px] leading-snug list-disc pl-4 marker:text-primary">{s.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
    </Section>
  );
}

// Expanded SKU row: merged timeline, heat overlay, install base by area, safety-stock derivation.
export default function SkuDetail({ sku, branch }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    getSku(sku, branch).then(x => alive && setD(x)).catch(e => alive && setError(e.message));
    return () => { alive = false; };
  }, [sku, branch]);
  if (error) return <div className="p-4 text-[12px] text-critical" data-testid="sku-detail-error">{error}</div>;
  if (!d) return <div className="p-6 text-center text-muted text-[12px]"><Loader2 size={13} className="inline animate-spin mr-2" />Loading SKU detail…</div>;
  const r = d.row;
  return (
    <div className="p-3 bg-surface-2/40 space-y-3 animate-fade-in" data-testid="sku-detail">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
        <span className="font-mono-tabular text-muted">Class path</span>
        <span className="font-mono-tabular">raw CV {r.cv_raw ?? "—"} → processed CV {r.cv_processed ?? "—"}</span>
        <span className="font-mono-tabular">{r.intermittent ? `${r.abc} · Z-Intermittent (${r.annual_units_raw} units / 12 mo)` : `${r.abc}${r.xyz_original} → ${r.final_class}`}</span>
        <span className="text-muted">{formatNumber(r.annual_units)} units / yr · on hand {formatNumber(r.on_hand)}</span>
        <OriginBadge {...d.origin} className="ml-auto" testid="sku-detail-origin" />
      </div>
      <ClassExplanation kind="sku" id={r.sku} branch={branch} testid="sku-explain" className="border border-primary/30 bg-primary/[0.04] rounded-sm p-3" />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <Timeline d={d} />
        <Heat d={d} />
        <Section icon={MapPin} title="Install base · compatible aircon units" testid="sku-detail-install"><InstallBaseTree ib={d.install_base} /></Section>
        <SafetyStock d={d} />
      </div>
    </div>
  );
}
